/**
 * Trustpilot Review Widget
 *
 * Main parent component (custom element <trustpilot-review-widget>).
 * - Fetches product reviews from the Trustpilot API (1 call on init, 1 on filter/sort, 1 per "Load more" when needed).
 * - Manages filter (rating) and sort state; provides data and methods for the list and media-tab child components.
 * - Renders the review list, stats bar, Load more button, and "X Ratings-Only Reviews" when all filtered reviews are shown.
 * - On page load, eagerly fetches reviews for Product JSON-LD (schema.org).
 */

const EMPTY_REVIEWS_RESPONSE = { reviews: [], total: 0, totalPages: 1, mediaReviews: [] };
const NO_MEDIA_HTML = '<div class="no-attachments">No media available</div>';
const VIDEO_PLACEHOLDER_HTML = '<div class="tp-video-placeholder" style="width:400px;height:400px;background:#000;display:flex;align-items:center;justify-content:center;color:#fff;">▶</div>';

class TrustpilotReviewWidget extends HTMLElement {
  constructor() {
    super();
    this.productId = window?.trustpilotProductId ?? null;
    this.storeName = window?.trustpilotStoreName ?? 'perfumania';
    this.reviewsApiBase = window?.trustpilotReviewsApiBase;

    this.reviews = [];
    this.total = 0;
    this.totalPages = 1;
    this.currentPage = 1;
    this.reviewsPerPage = window?.trustpilotReviewsPerPage ?? 12;
    this.currentSort = 'recent-desc';
    this.currentRateFilter = '';
    this.initialized = false;
    this.allReviewsLoaded = false;
    this.mediaReviews = [];
    this.displayedCount = 0;
    this.filteredTotal = 0;
    this.ratingTotal = 0;
    this._reviewsCache = new Map();
    this._schemaApplied = false;

    this.announcements = {
      loading: 'Loading reviews, please wait.',
      loaded: 'Reviews loaded successfully.',
      loadError: 'Unable to load reviews. Please try again later.',
      loadMore: 'Loading more reviews.',
      allLoaded: 'All reviews have been loaded.',
      noReviews: 'No reviews found matching your criteria.',

      reviewsShown: (start, end, total) => `Showing ${start} to ${end} of ${total} reviews.`,
      filterApplied: (rate) => `Filter applied: ${!rate || rate === 'all' ? 'All ratings' : `${rate} star${rate === '1' ? '' : 's'}`}.`,
      sortApplied: (sort) => {
        const names = {
          'recent-desc': 'Most Recent',
          'recent-asc': 'First Added',
          'rating-desc': 'Highest to Lowest Rating',
          'rating-asc': 'Lowest to Highest Rating',
        };
        return `Sorted by: ${names[sort] || sort}.`;
      },
    };
  }

  connectedCallback() {
    this.applyProductSchema();
    if (this.initialized) return;
    this.setupInitOnReviewsTabOpen();
  }

  /** Fetches the first page of reviews and merges text reviews into Product JSON-LD. */
  async applyProductSchema() {
    if (this._schemaApplied || !this.productId || !this.reviewsApiBase) return;

    const script = document.querySelector('script[data-product-json-ld-schema]');
    if (!script) return;

    this._schemaApplied = true;

    const { reviews } = await this.fetchReviews({ page: 1, requireAllMedia: false, limit: 5 });
    const reviewItems = (reviews ?? []).slice(0, 5).map((review) => this.buildSchemaReview(review)).filter(Boolean);

    if (!reviewItems.length) return;

    try {
      const schema = JSON.parse(script.textContent);
      const schemaRatingValue = Number.parseFloat(script.getAttribute('data-schema-rating-value') ?? '');
      const schemaReviewCount = Number.parseInt(script.getAttribute('data-schema-review-count') ?? '', 10);

      schema.review = reviewItems;

      if (Number.isFinite(schemaRatingValue) && schemaReviewCount > 0) {
        schema.aggregateRating = {
          "@type": "AggregateRating",
          "ratingValue": schemaRatingValue,
          "reviewCount": schemaReviewCount,
          "ratingCount": schemaReviewCount,
        };
      }
      script.textContent = JSON.stringify(schema);
    } catch (err) {
      console.error('Product JSON-LD parse error:', err);
    }
  }

  /** Builds a schema Review from API fields only (no static fallbacks). */
  buildSchemaReview(review) {
    const body = String(review.reviewContent ?? review.content ?? '').trim();
    if (!body || this.normalizeReviewContent(body) === '') return null;
    if (review.rating == null) return null;

    const author = review.author ?? review.consumer?.displayName;
    if (!author) return null;

    const dateRaw = review.reviewCreateDate ?? review.createdAt;
    const datePublished = dateRaw ? new Date(dateRaw).toISOString().slice(0, 10) : null;

    const schema = {
      "@type": "Review",
      "author": author,
      // "name": body,
      "reviewBody": body,
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": String(review.rating),
      },
      "datePublished": datePublished,
    };

    return schema;
  }

  /**
   * Defers init until the Reviews tab is open (avoids unnecessary API call if the user never opens the tab).
   * Runs init on first tab click, or immediately if the tab is already active.
   */
  setupInitOnReviewsTabOpen() {
    const panel = this.closest('#tab4-tab');
    const tabBtn = document.querySelector('.product-reviews-tab, #tab4');
    const mobileCtrl = panel?.querySelector('.mobile-accordion-controller');

    const runOnce = () => {
      if (this.initialized) return;

      tabBtn?.removeEventListener('click', runOnce);
      mobileCtrl?.removeEventListener('click', runOnce);
      this.init();
    };

    if (panel?.classList.contains('is-active')) {
      runOnce();
      return;
    }

    if (tabBtn) tabBtn.addEventListener('click', runOnce);
    if (mobileCtrl) mobileCtrl.addEventListener('click', runOnce);
  }

  /**
   * Initializes the widget: fetches the first page of reviews (1 API call), renders the list and media section,
   * sets window.trustpilotReviewsLoader, binds the Load more button, and dispatches 'reviews-loaded' for the tab.
   */
  async init() {
    if (this.initialized || !this.productId) return;

    this.announce(this.announcements.loading);
    this.currentPage = 1;
    this.toggleMediaSkeleton(true);

    await this.fetchFirstPage();

    if (this.reviews.length === 0) {
      this.announce(this.announcements.loadError, 'assertive');
      this.toggleMediaSkeleton(false);
      this.toggleReviewsCardSkeleton(false);
      this.showNoReviewsState('initial');
      this.setupNoReviewsListeners();
      this.initialized = true;
      return;
    }

    this.announce(this.announcements.loaded);
    this.toggleReviewsCardSkeleton(false);
    window.trustpilotReviewsLoader = this;
    this._setCacheFromState();

    if (!window.trustpilotReviewModal) {
      const modalEl = this.querySelector('[data-tp-modal]');
      if (modalEl && window.TrustpilotReviewModal) {
        window.trustpilotReviewModal = new window.TrustpilotReviewModal(modalEl);
        window.trustpilotReviewModal.init();
      }
    }

    this.displayedCount = Math.min(this.reviewsPerPage, this.reviews.length);
    this.showNoReviewsState(null);
    this.setupNoReviewsListeners();
    this.renderReviews();
    this.syncSortDropdownToMostRecent();

    const loadMoreBtn = this.querySelector('[data-load-more-btn]');
    if (loadMoreBtn && !loadMoreBtn.dataset.listenerAttached) {
      loadMoreBtn.addEventListener('click', () => this.loadMore());
      loadMoreBtn.dataset.listenerAttached = 'true';
    }

    this.initialized = true;
    this.dispatchEvent(
      new CustomEvent('reviews-loaded', { detail: { reviews: this.reviews }, bubbles: true })
    );
  }

  async fetchReviews(options = {}) {
    const { productId: overrideId, page = this.currentPage, requireAllMedia = false, limit: limitOverride } = options;
    const id = overrideId ?? this.productId;
    const limit = limitOverride ?? this.reviewsPerPage;

    if (!id || !this.reviewsApiBase) { return EMPTY_REVIEWS_RESPONSE; }

    const params = new URLSearchParams({
      page: String(page),
      storeName: this.storeName,
      limit: String(limit),
      requireAllMedia: requireAllMedia ? 'true' : 'false',
    });

    if (this.currentSort) {
      const { sortBy, sortOrder } = this.getSortParams();
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);
    }
    
    if (this.currentRateFilter && this.currentRateFilter !== 'all') { params.set('rate', this.currentRateFilter); }

    const headers = { 'Content-Type': 'application/json' };
    if (this.reviewsApiBase.includes('ngrok')) { headers['ngrok-skip-browser-warning'] = 'true'; }

    try {
      const res = await fetch(`${this.reviewsApiBase}/${id}?${params}`, {
        method: 'GET',
        mode: 'cors',
        headers,
      });

      if (!res.ok) throw new Error(`API error: ${res.status}`);

      const data = await res.json();
      const reviews = data?.result?.reviews ?? [];

      return {
        reviews,
        total: data?.result?.total ?? reviews.length,
        totalPages: data?.result?.totalPages ?? 1,
        mediaReviews: data?.result?.media?.reviews ?? [],
        filteredTotal: data?.result?.filteredTotal,
        ratingTotal: data?.result?.ratingTotal,
      };
    } catch (err) {
      console.error('Error fetching reviews:', err);
      return EMPTY_REVIEWS_RESPONSE;
    }
  }

  async fetchFirstPage() {
    const payload = await this.fetchReviews({ page: 1, requireAllMedia: true });

    this.reviews = payload.reviews ?? [];
    this.total = payload.total ?? this.reviews.length;
    this.totalPages = payload.totalPages ?? 1;
    this.currentPage = 1;
    this.mediaReviews = payload.mediaReviews ?? [];
    if (payload.filteredTotal != null) this.filteredTotal = payload.filteredTotal;
    if (payload.ratingTotal != null) this.ratingTotal = payload.ratingTotal;
  }

  /**
   * Renders the visible slice of reviews into [data-reviews-list], updates the stats bar (e.g. "1 - 12 of 143 Reviews"),
   * shows or hides the Load more button based on allReviewsLoaded, and the "X Ratings-Only Reviews" line when applicable.
   */
  renderReviews() {
    const container = this.querySelector('[data-reviews-list]');
    if (!container) return;

    if (!this.reviews?.length) {
      this.showNoReviewsState('filter');
      container.innerHTML = '';
      return;
    }

    const toShow = this.reviews.slice(0, this.displayedCount);
    const cards = toShow.map((r) => this.buildReviewCard(r)).filter(Boolean);
    if (cards.length === 0) {
      this.showNoReviewsState('filter');
      container.innerHTML = '';
      return;
    }

    this.showNoReviewsState(null);
    container.innerHTML = cards.join('');

    const visibleCount = cards.length;

    const statsBar = this.querySelector('[data-review-stats-bar]');
    const statsBarMobile = this.querySelector('[data-review-stats-bar-mobile]');
    if (statsBar) {
      statsBar.textContent = `1 - ${visibleCount} of ${this.total} Reviews`;
      this.announce(this.announcements.reviewsShown(1, visibleCount, this.total));
    }
    if (statsBarMobile) {
      statsBarMobile.textContent = `1 - ${visibleCount} of ${this.total} Reviews`;
    }

    const loadMoreBtn = this.querySelector('[data-load-more-btn]');
    const effectiveTotal = (this.filteredTotal != null && this.filteredTotal > 0) ? this.filteredTotal : this.total;
    this.allReviewsLoaded = this.currentPage >= this.totalPages || this.displayedCount >= effectiveTotal;
    if (loadMoreBtn) {
      loadMoreBtn.style.display = this.allReviewsLoaded ? 'none' : 'block';
      if (this.allReviewsLoaded) this.announce(this.announcements.allLoaded);
    }

    const ratingsOnlyEl = this.querySelector('[data-tp-ratings-only-count]');
    if (ratingsOnlyEl) {
      const showRatingsOnly = this.allReviewsLoaded && this.ratingTotal > 0;
      ratingsOnlyEl.classList.toggle('hidden', !showRatingsOnly);
      if (showRatingsOnly) {
        ratingsOnlyEl.textContent = `${this.ratingTotal} Ratings-Only Reviews`;
      }
    }
  }

  /**
   * Clears filter and sort state and dropdown UI (selected options), then refetches page 1 and re-renders.
   * Used when the user clicks "Clear filters".
   */
  resetDropdownsAndRefetch() {
    this.currentRateFilter = '';
    this.currentSort = '';
    this.currentPage = 1;

    const listSection = this.querySelector('[data-reviews-list-section]');
    if (listSection) {
      listSection.querySelectorAll('[data-dropdown]').forEach((dd) => {
        dd.querySelectorAll('[data-selected-text]').forEach((el) => {
          el.textContent = '';
        });

        dd.querySelectorAll('[data-dropdown-option].selected').forEach((opt) => {
          opt.classList.remove('selected');
          opt.setAttribute('aria-selected', 'false');
        });
      });
    }
    this.announce(this.announcements.filterApplied(''));
    return this.refetchAndRender();
  }

  showNoReviewsState(state) {
    const main = this.querySelector('[data-reviews-main-content]');
    const loadMoreBtn = this.querySelector('[data-load-more-btn]');
    const noInitial = this.querySelector('[data-tp-no-reviews-initial]');
    const noFilter = this.querySelector('[data-tp-no-reviews-filter]');
    const mediaSection = this.querySelector('.trustpilot-widget-tab-section');

    if (state === 'initial') {
      this.setVisible(main, false);
      if (loadMoreBtn) loadMoreBtn.style.display = 'none';
      this.setVisible(noInitial, true);
      this.setVisible(noFilter, false);
      if (mediaSection) mediaSection.style.display = '';
      return;
    }

    if (state === 'filter') {
      this.setVisible(main, false);
      if (loadMoreBtn) loadMoreBtn.style.display = 'none';
      this.setVisible(noInitial, false);
      this.setVisible(noFilter, true);
      if (mediaSection) mediaSection.style.display = '';
      return;
    }

    this.setVisible(main, true);
    this.setVisible(noInitial, false);
    this.setVisible(noFilter, false);

    if (mediaSection) mediaSection.style.display = '';
    if (loadMoreBtn) loadMoreBtn.style.display = this.allReviewsLoaded ? 'none' : 'block';
  }

  /** Show/hide media section skeleton; when showing, hide slider and empty-state messages. */
  toggleMediaSkeleton(show) {
    const skeleton = this.querySelector('[data-skeleton-media-items]');
    const slider = this.querySelector('[data-trustpilot-slider]');
    const emptyImages = this.querySelector('[data-tp-media-empty-state="images"]');
    const emptyVideos = this.querySelector('[data-tp-media-empty-state="videos"]');

    if (skeleton) { 
      skeleton.classList.toggle('hidden', !show);
    }
    if (show) {
      if (slider) slider.classList.add('hidden');
      if (emptyImages) emptyImages.classList.add('hidden');
      if (emptyVideos) emptyVideos.classList.add('hidden');
    }
  }

  /** Show/hide review list skeleton. When show=true, also hide Load more and Ratings-Only so only skeleton is visible. */
  toggleReviewsCardSkeleton(show, hideListWhenShowing = false) {
    const skeleton = this.querySelector('[data-skeleton-reviews]');
    const list = this.querySelector('[data-reviews-list]');
    const loadMoreBtn = this.querySelector('[data-load-more-btn]');
    const ratingsOnlyEl = this.querySelector('[data-tp-ratings-only-count]');

    if (skeleton) skeleton.classList.toggle('hidden', !show);
    if (show && hideListWhenShowing && list) list.classList.add('hidden');
    if (!show && list) list.classList.remove('hidden');

    /** When skeleton is shown, hide Load more and Ratings-Only so only skeleton is visible. */
    if (show) {
      if (loadMoreBtn) loadMoreBtn.style.display = 'none';
      if (ratingsOnlyEl) ratingsOnlyEl.classList.add('hidden');
    }
  }

  /**
   * Shows more reviews: either by revealing more from already-fetched data (no API call) or by fetching the next page.
   * Does not fetch beyond totalPages. Media for the carousel is not updated here (it comes from the first API call only).
   */
  async loadMore() {
    const effectiveTotal = (this.filteredTotal != null && this.filteredTotal > 0) ? this.filteredTotal : this.total;
    if (this.displayedCount >= effectiveTotal) return;
    if (this.currentPage >= this.totalPages) {
      this.displayedCount = this.reviews.length;
      this.renderReviews();
      return;
    }

    const nextDisplayed = Math.min(this.displayedCount + this.reviewsPerPage, effectiveTotal);
    const container = this.querySelector('[data-reviews-list]');
    if (!container) return;

    const loadMoreBtn = this.querySelector('[data-load-more-btn]');
    if (loadMoreBtn) loadMoreBtn.style.display = 'none';

    if (nextDisplayed <= this.reviews.length) {
      this.displayedCount = nextDisplayed;
      this.renderReviews();
      return;
    }

    const nextPage = this.currentPage + 1;
    if (nextPage > this.totalPages) {
      this.displayedCount = this.reviews.length;
      this.renderReviews();
      return;
    }

    this.toggleReviewsCardSkeleton(true);
    this.announce(this.announcements.loadMore);

    const payload = await this.fetchReviews({ page: nextPage });
    if (payload.reviews?.length) {
      this.currentPage = nextPage;
      this.reviews = [...this.reviews, ...payload.reviews];
      this.total = payload.total ?? this.total;
      this.totalPages = payload.totalPages ?? 1;
    }

    this.displayedCount = Math.min(nextDisplayed, this.reviews.length);
    this.renderReviews();
    this.toggleReviewsCardSkeleton(false);
  }

  /** Used after filter/sort change: refetch first page (1 API call), re-render list and media section. */
  async refetchAndRender() {
    const cached = this._getCachedData();
    if (cached) {
      this._applyCachedData(cached);
      this.displayedCount = Math.min(this.reviewsPerPage, this.reviews.length);
      this.renderReviews();
      await this.refreshMediaSection();
      return;
    }

    this.toggleReviewsCardSkeleton(true, true);
    this.toggleMediaSkeleton(true);
    await this.fetchFirstPage();

    this._setCacheFromState();
    this.displayedCount = Math.min(this.reviewsPerPage, this.reviews.length);
    this.renderReviews();
    this.toggleReviewsCardSkeleton(false);
    await this.refreshMediaSection();
  }

  /** Ask tab component to rebuild slider content (after filter/sort). */
  async refreshMediaSection() {
    const section = this.querySelector('.trustpilot-widget-tab-section');
    if (!section) return;

    const tab = section.querySelector('trustpilot-review-widget-tab');
    if (tab && typeof tab.refreshContent === 'function') {
      await tab.refreshContent();
    }
  }

  /** Called by the list component when the user selects a rating filter (e.g. "5 stars"). Refetches with the new rate. */
  async setRateFilter(rate) {
    this.currentRateFilter = rate ?? '';
    this.currentPage = 1;
    this.announce(this.announcements.filterApplied(this.currentRateFilter));
    await this.refetchAndRender();
  }

  /** Called by the list component when the user selects a sort (e.g. "Most Recent"). Refetches with the new sort. */
  async setReviewSort(sort) {
    this.currentSort = sort ?? '';
    this.currentPage = 1;
    if (this.currentSort) this.announce(this.announcements.sortApplied(this.currentSort));
    await this.refetchAndRender();
  }

  /** Syncs the sort dropdown UI to the default "Most Recent" (recent-desc) on initial load. */
  syncSortDropdownToMostRecent() {
    const sortDropdown = this.querySelector('[data-dropdown-type="sort"]');
    if (!sortDropdown) return;
    const option = sortDropdown.querySelector('[data-dropdown-option][data-value="recent-desc"]');
    if (!option) return;
    sortDropdown.querySelectorAll('[data-selected-text]').forEach((el) => {
      el.textContent = 'Most Recent';
    });
    sortDropdown.querySelectorAll('[data-dropdown-option]').forEach((opt) => {
      opt.classList.remove('selected');
      opt.setAttribute('aria-selected', 'false');
    });
    option.classList.add('selected');
    option.setAttribute('aria-selected', 'true');
  }

  /** Public alias used by the Liquid "Clear filters" button. Clears dropdowns and refetches. */
  async clearFiltersAndRefetch() {
    return this.resetDropdownsAndRefetch();
  }

  /** Binds the "Clear filters" button so it calls clearFiltersAndRefetch (once per element). */
  setupNoReviewsListeners() {
    const btn = this.querySelector('[data-tp-clear-filters]');
    if (!btn || btn.dataset.listenerAttached) return;

    btn.addEventListener('click', () => this.clearFiltersAndRefetch());
    btn.dataset.listenerAttached = 'true';
  }

  /** Build block */
  buildReviewCard(review) {
    const rating = review.rating ?? 0;
    const stars = this.starsForRating(rating);
    const author = review.author ?? review.consumer?.displayName ?? 'Anonymous';
    const date = review.reviewCreateDate ?? review.createdAt;
    const timeAgo = this.formatTime(date);
    const content = this.normalizeReviewContent(review.reviewContent ?? review.content ?? '');
    const title = this.extractTitle(content);
    const timestamp = new Date(date).getTime() / 1000;
    const reviewId = review.id ?? `review-${timestamp}`;
    const mediaHtml = this.buildMediaCarousel(
      review.imagesLink ?? [],
      review.videosLink ?? [],
      author,
      content,
      date,
      rating
    );
    return `<article class="review-card" data-review-card data-rating="${rating}" data-timestamp="${timestamp}" role="article" aria-labelledby="review-title-${reviewId}">
              <div class="reviewer-details"><span class="reviewer-name">${author}</span></div>
              <div class="review-main-content">
                <div class="review-meta-left">
                  <div class="review-stars-text" aria-label="${rating} out of 5 stars">${stars}</div>
                  <h3 class="review-title" id="review-title-${reviewId}">${title}</h3>
                  <div class="reviewer-name mobile-only-text">${author}</div>
                  <time class="review-time" datetime="${date}">${timeAgo}</time>
                </div>
                <div class="review-body-row">
                  <div class="review-text">${content}</div>
                  ${mediaHtml}
                </div>
              </div>
            </article>`;
  }

  /** Builds the HTML for one media slide (image or video placeholder) used in carousels and the main media tab. */
  buildMediaItemHtml(opts) {
    const {
      url,
      isVideo,
      reviewerName,
      timeAgo,
      reviewTitle,
      escapedReviewFull,
      escapedReviewShort,
      rating,
      imgAlt,
    } = opts;

    const mediaType = isVideo ? 'video' : 'image';
    const escapedImage = this.escapeAttr(isVideo ? '' : url);
    const escapedVideo = isVideo ? this.escapeAttr(url) : '';
    const alt = imgAlt ?? (isVideo ? 'Review Video' : 'Review Image');
    const imgOrPlaceholder = isVideo
      ? VIDEO_PLACEHOLDER_HTML
      : `<img class="tp-review-img" src="${this.escapeAttr(url)}" alt="${this.escapeAttr(alt)}" loading="lazy" width="400" height="400" onload="this.classList.add('loaded')">`;
    const playIcon = isVideo ? '<div class="tp-play-icon" aria-hidden="true">▶</div>' : '';

    return `<div class="trustpilot-item" data-trustpilot-item>
              <div class="tp-item-inner" data-tp-item-inner data-media-type="${mediaType}" data-image="${escapedImage}" ${isVideo ? `data-video="${escapedVideo}"` : ''}
                data-user="${reviewerName}" data-review="${escapedReviewFull}" data-review-short="${escapedReviewShort}" data-title="${reviewTitle}" data-time="${timeAgo}" data-rating="${rating}"
                aria-label="View ${reviewerName}'s review ${mediaType}" role="button" tabindex="0">
                ${imgOrPlaceholder}
                ${playIcon}
              </div>
            </div>`;
}

  buildMediaCarousel(
    imagesLink,
    videosLink,
    reviewerName,
    reviewContent,
    reviewCreatedAt,
    reviewStars
  ) {
    const mediaUrls = [];
    (imagesLink || []).forEach((url) => {
      if (url) mediaUrls.push({ url, isVideo: false });
    });
    (videosLink || []).forEach((url) => {
      if (url) mediaUrls.push({ url, isVideo: true });
    });
    if (!mediaUrls.length) return '';

    const normalizedContent = this.normalizeReviewContent(reviewContent);
    const timeAgo = this.formatTime(reviewCreatedAt ?? new Date().toISOString());
    const reviewTitle = this.extractTitle(normalizedContent) || 'Review Media';
    const escapedFull = this.escapeAttr(normalizedContent || `${reviewerName}'s review`);
    const escapedShort = this.escapeAttr(
      (normalizedContent || `${reviewerName}'s review`).substring(0, 100)
    );
    const rating = reviewStars ?? 5;

    const itemsHtml = mediaUrls
      .map(({ url, isVideo }) =>
        this.buildMediaItemHtml({
          url,
          isVideo,
          reviewerName,
          timeAgo,
          reviewTitle,
          escapedReviewFull: escapedFull,
          escapedReviewShort: escapedShort,
          rating,
        })
      )
      .join('');

    return `<div class="review-item-media">
              <trustpilot-review-widget-tab class="trustpilot-tab-content" data-tp-slider="review-card" data-item-desktop="3" data-item-mobile="3">
                <div class="trustpilot-slider" data-trustpilot-slider>${itemsHtml}</div>
              </trustpilot-review-widget-tab>
            </div>`;
  }

  buildAllAttachmentsForTab(container) {
    const el = container?.nodeType === 1 ? container : null;
    if (!el) return;

    const attachments = this.getAllAttachments();
    if (!attachments?.length) {
      el.innerHTML = NO_MEDIA_HTML;
      return;
    }

    const limit = attachments.slice(0, 10);
    const mediaItemsHtml = limit
      .map(({ url, isVideo, review }) => {
        const author = review.author ?? 'Anonymous';
        const content = this.normalizeReviewContent(review.reviewContent ?? '');

        return this.buildMediaItemHtml({
          url,
          isVideo,
          reviewerName: author,
          timeAgo: this.formatTime(
            review.reviewCreateDate ?? review.createdAt ?? new Date().toISOString()
          ),
          reviewTitle: this.extractTitle(content),
          escapedReviewFull: this.escapeAttr(content),
          escapedReviewShort: this.escapeAttr(content).substring(0, 100),
          rating: review.rating ?? 5,
          imgAlt: isVideo ? undefined : `${author}'s review image`,
        });
      })
      .join('');

    el.querySelector('[data-loading-attachments]')?.remove();
    el.innerHTML = mediaItemsHtml;
  }

  /** Escapes double quotes in a string for safe use in HTML attributes. */
  escapeAttr(str) {
    return (str ?? '').replace(/"/g, '&quot;');
  }

  /** Returns a string of star characters (★/☆) for the given rating (0–5). */
  starsForRating(rating) {
    const n = Math.max(0, Math.min(5, Math.floor(rating ?? 0)));
    return '★'.repeat(n) + '☆'.repeat(5 - n);
  }

  /** Toggles the .hidden class on an element. */
  setVisible(el, visible) {
    if (el) el.classList.toggle('hidden', !visible);
  }

  /** Parses currentSort (e.g. "recent-desc") into { sortBy, sortOrder } for the API. */
  getSortParams() {
    const [sortBy, sortOrder] = (this.currentSort || '').split('-');
    if (sortBy === 'recent' || sortBy === 'rating') {
      return { sortBy, sortOrder: sortOrder === 'asc' ? 'asc' : 'desc' };
    }
    return { sortBy: 'recent', sortOrder: 'desc' };
  }

  /**
   * Collects all image and video attachments from mediaReviews (or reviews if no mediaReviews), sorted by review order.
   * Used by buildAllAttachmentsForTab for the main media slider.
   */
  getAllAttachments() {
    const source = (this.mediaReviews?.length > 0 ? this.mediaReviews : this.reviews) ?? [];
    const out = [];
    source.forEach((review) => {
      (review.imagesLink || []).forEach((url) => {
        if (url) out.push({ url, isVideo: false, review });
      });
      (review.videosLink || []).forEach((url) => {
        if (url) out.push({ url, isVideo: true, review });
      });
    });

    const reviewOrder = new Map();
    (this.reviews ?? []).forEach((r, i) => {
      const id = r.id ?? r.reviewId ?? i;
      reviewOrder.set(id, i);
    });

    const getOrder = (r) => reviewOrder.get(r.id ?? r.reviewId) ?? 9999;
    out.sort((a, b) => getOrder(a.review) - getOrder(b.review));
    return out;
  }

  /** Formats a date string as a relative time (e.g. "2 days ago", "1 month ago"). */
  formatTime(dateString) {
    if (!dateString) return '';
    const diffDays = Math.floor((Date.now() - new Date(dateString)) / (1000 * 60 * 60 * 24));

    if (diffDays >= 365) {
      const years = Math.floor(diffDays / 365);
      return `${years} year${years === 1 ? '' : 's'} ago`;
    }

    if (diffDays >= 30) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months === 1 ? '' : 's'} ago`;
    }

    if (diffDays >= 1) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    return 'today';
  }

  normalizeReviewContent(content) {
    if (content == null) return '';
    const s = String(content).trim().toLowerCase();
    if (
      s === '' ||
      s.includes('no-description') ||
      s.includes('--no description--') ||
      s.includes('no description') ||
      s.includes('no description available')
    )
      return '';
    return s;
  }

  /** Extracts a short title from the review content (first sentence, max 100 chars). */
  extractTitle(content) {
    if (!content) return '';
    const first = content.split(/[.!?]/)[0]?.trim();

    if (!first?.length) return '';
    return first.length > 100 ? first.substring(0, 100) + '...' : first;
  }

  announce(message, priority = 'polite', clearAfter = true) {
    const live = document.getElementById('trustpilot-aria-live');
    if (!live) return;

    live.setAttribute('aria-live', priority);
    live.textContent = '';

    setTimeout(() => {
      live.textContent = message;
      if (clearAfter) {
        const clearDelay = priority === 'assertive' ? 2000 : 1500;
        setTimeout(() => {
          live.textContent = '';
        }, clearDelay);
      }
    }, 100);
  }

  _getCacheKey() {
    const id = this.productId;
    if (!id || !this.reviewsApiBase) return '';
    const params = new URLSearchParams({
      page: '1',
      storeName: this.storeName,
      limit: String(this.reviewsPerPage),
      requireAllMedia: 'true',
    });
    if (this.currentSort) {
      const { sortBy, sortOrder } = this.getSortParams();
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);
    }
    if (this.currentRateFilter && this.currentRateFilter !== 'all') params.set('rate', this.currentRateFilter);
    return `${this.reviewsApiBase}/${id}?${params}`;
  }

  _getCachedData() {
    const key = this._getCacheKey();
    return key ? (this._reviewsCache.get(key) ?? null) : null;
  }

  _applyCachedData(entry) {
    this.reviews = entry.reviews ?? [];
    this.total = entry.total ?? 0;
    this.totalPages = entry.totalPages ?? 1;
    this.currentPage = 1;
    this.mediaReviews = entry.mediaReviews ?? [];
    if (entry.filteredTotal != null) this.filteredTotal = entry.filteredTotal;
    if (entry.ratingTotal != null) this.ratingTotal = entry.ratingTotal;
  }

  _setCacheFromState() {
    const key = this._getCacheKey();
    if (!key) return;
    this._reviewsCache.set(key, {
      reviews: [...this.reviews],
      total: this.total,
      totalPages: this.totalPages,
      mediaReviews: [...(this.mediaReviews || [])],
      filteredTotal: this.filteredTotal,
      ratingTotal: this.ratingTotal,
    });
  }
}

if (!customElements.get('trustpilot-review-widget')) {
  customElements.define('trustpilot-review-widget', TrustpilotReviewWidget);
}
